import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { trackVisit } from "@/data/analyticsStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useSwapStore } from "@/stores/swapStore";
import { supabase } from "@/integrations/supabase/client";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";
import { getEffectiveTheme } from "@/lib/themesEnabledFlag";
import { useSessionRole } from "@/hooks/useSessionRole";
import { useAuth } from "@/hooks/useAuth";
import { setAuthGateReturn } from "@/pages/AuthGate";
import { hasGuestPass } from "@/lib/guestPass";

import ClassicStorefront from "@/components/ClassicStorefront";
import MinimalStorefront from "@/components/MinimalStorefront";
const ModernStorefront = lazy(() => import("@/components/ModernStorefront"));
const LiteStorefront = lazy(() => import("@/components/LiteStorefront"));

const Index = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Storefront role awareness — staff/owner sessions can preview the
  // storefront (e.g. in a separate tab) without being redirected to /admin.
  // Session-isolation in customer-only widgets already blocks cross-context
  // writes, so it's safe to render the storefront for any session.
  const { context: sessionContext } = useSessionRole();
  void sessionContext;


  const REFERRAL_KEY = "khays_active_referral";
  const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  // ─── Referral context resolution (URL is single source of truth) ───
  // Rules:
  //  1. ?ref=<name> in URL  → activate referral context, persist to storage.
  //  2. No ?ref= in URL but a valid persisted referral exists → redirect once
  //     to add ?ref=<name> so the URL always reflects the active context.
  //     This prevents the main storefront from silently using referral pricing.
  //  3. No ?ref= and no persisted referral → MAIN storefront, never apply
  //     commission anywhere.
  const urlRef = searchParams.get("ref");

  // Read persisted referral once on mount (synchronous so first render is correct).
  const [persistedRef] = useState<string | undefined>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(REFERRAL_KEY) || "null");
      if (saved?.ref && Date.now() - (saved.savedAt || 0) < REFERRAL_TTL_MS) {
        return String(saved.ref);
      }
    } catch {}
    return undefined;
  });

  // If URL has no ref but storage has one, push ref into URL (single redirect).
  useEffect(() => {
    if (!urlRef && persistedRef) {
      const sp = new URLSearchParams(searchParams);
      sp.set("ref", persistedRef);
      navigate({ pathname: location.pathname, search: `?${sp.toString()}` }, { replace: true });
    }
  }, [urlRef, persistedRef, navigate, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawRef = useMemo(() => {
    if (!urlRef) return undefined;
    const cleaned = decodeURIComponent(urlRef).trim().replace(/\s+/g, "");
    if (!cleaned) return undefined;
    try {
      localStorage.setItem(REFERRAL_KEY, JSON.stringify({ ref: cleaned, savedAt: Date.now() }));
    } catch {}
    return cleaned;
  }, [urlRef]);

  const [referralStaff, setReferralStaff] = useState<string | undefined>(undefined);
  const [referralInvalid, setReferralInvalid] = useState(false);
  const [referralChecked, setReferralChecked] = useState(!rawRef);
  const [staffCommissionPercent, setStaffCommissionPercent] = useState(0);

  const sfSettings = useStorefrontSettings();
  const swapSettings = useSwapStore(s => s.settings);
  const isLocked = sfSettings.storefrontLocked === true;

  // Reactive theme — subscribe with selectors so theme switching re-renders Index immediately.
  // `getEffectiveTheme` forces "lite" unless the owner-only `khays_themes_enabled`
  // flag is on (and never for tenants). This eliminates the brief Classic
  // flash before cloudSync hydrates and keeps non-Lite themes hidden by default.
  const adminDefault = useStorefrontTheme(s => s.adminDefault);
  const visitorTheme = useStorefrontTheme(s => s.visitorTheme);
  const visitorToggleEnabled = useStorefrontTheme(s => s.visitorToggleEnabled);
  const activeTheme = getEffectiveTheme(adminDefault, visitorTheme, visitorToggleEnabled);

  // ─── Failsafe reset (debug/support hook) ───
  useEffect(() => {
    (window as any).__khaysClearReferral = () => {
      try { localStorage.removeItem(REFERRAL_KEY); } catch {}
      const sp = new URLSearchParams(window.location.search);
      sp.delete("ref");
      window.location.replace(`${window.location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}`);
    };
  }, []);

  // ─── Referral validation ───
  // Initial validate on rawRef change. If URL has no ref, immediately clear
  // any stale referral state so main storefront never inherits commission.
  useEffect(() => {
    if (!rawRef) {
      setReferralStaff(undefined);
      setReferralInvalid(false);
      setStaffCommissionPercent(0);
      setReferralChecked(true);
      return;
    }
    let cancelled = false;
    const validate = () => {
      supabase.rpc("validate_referral", { _username: rawRef }).then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data && data.length > 0) {
          setReferralStaff(rawRef);
          setReferralInvalid(false);
          setStaffCommissionPercent(Number(data[0].commission_percent ?? 0));
        } else {
          setReferralStaff(undefined);
          setReferralInvalid(true);
          setStaffCommissionPercent(0);
          try { localStorage.removeItem(REFERRAL_KEY); } catch {}
        }
        setReferralChecked(true);
      });
    };
    validate();
    // Live admin sync: re-validate every 30s and on focus/visibility so
    // commission_percent updates from admin propagate without manual refresh.
    const interval = window.setInterval(validate, 30_000);
    const onFocus = () => validate();
    const onVis = () => { if (document.visibilityState === "visible") validate(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [rawRef]);


  useEffect(() => {
    import("@/lib/seo").then(({ applySeo }) => applySeo({
      title: "Khay's Devices — Quality-Graded Phones in Ghana",
      description: "Pre-order quality-inspected, professionally graded phones, tablets and accessories in Ghana. Transparent grading and competitive Cedi pricing.",
      canonicalPath: "/",
    }));
    // Defer analytics off the critical boot path to reduce Max Potential FID.
    const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
    if (ric) {
      const handle = ric(() => trackVisit("/"), { timeout: 4000 });
      return () => (window as any).cancelIdleCallback?.(handle);
    }
    const t = setTimeout(() => trackVisit("/"), 1500);
    return () => clearTimeout(t);
  }, []);

  const isStaffLocked = referralStaff ? (sfSettings.lockedStaffUsernames || []).includes(referralStaff) : false;
  const referralPending = !!rawRef && !referralChecked;
  // While we're still resolving an unvalidated referral, hold the restricted
  // screen back — otherwise the storefront briefly flashes the lockdown UI on
  // every first load of /?ref=… before the validator finishes.
  const showContactOnly = isLocked || referralInvalid || isStaffLocked;

  // ─── Hard auth gate: redirect unauthenticated visitors to /auth-gate ───
  const { user, loading: authLoading } = useAuth();
  // A guest pass downgrades the hard gate to soft (browse now, sign in to buy).
  const hardGateActive = !!sfSettings.storefrontAuthRequired && (sfSettings.storefrontAuthMode || "soft") === "hard" && !hasGuestPass();
  const needsAuthRedirect = hardGateActive && !authLoading && !user && !showContactOnly;
  useEffect(() => {
    if (!needsAuthRedirect) return;
    const here = `${location.pathname}${location.search}`;
    setAuthGateReturn(here);
    navigate(`/auth-gate${rawRef ? `?ref=${encodeURIComponent(rawRef)}` : ""}`, { replace: true });
  }, [needsAuthRedirect, navigate, location.pathname, location.search, rawRef]);
  if (needsAuthRedirect) {
    return <div className="min-h-screen bg-background" />;
  }
  // Hold render while a referral is still being validated so we don't flash
  // either the storefront or the restricted screen on first load.
  if (referralPending) {
    return <div className="min-h-screen bg-background" />;
  }

  // While a staff/admin session is being detected (or redirect is in flight),
  // never render the storefront — that would briefly leak the staff session
  // into customer-only widgets like LiteStorefront / favorites sync.
  // Staff/owner sessions are allowed to browse the storefront for preview
  // purposes (e.g. opening it in a second tab while admin is in another).
  // We no longer block render or redirect — widgets already isolate session
  // context for any writes (favorites, cart, etc.).

  // ─── Lite theme: marketplace-style replica (image-blueprint locked) ───
  if (activeTheme === "lite") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <LiteStorefront
          referralStaff={referralStaff}
          referralInvalid={referralInvalid}
          isStaffLocked={isStaffLocked}
          showContactOnly={showContactOnly}
          staffCommissionPercent={staffCommissionPercent}
          rawRef={rawRef}
        />
      </Suspense>
    );
  }

  // ─── Modern theme: bottom-tab marketplace UI ───
  if (activeTheme === "modern") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <ModernStorefront
          referralStaff={referralStaff}
          referralInvalid={referralInvalid}
          isStaffLocked={isStaffLocked}
          showContactOnly={showContactOnly}
          staffCommissionPercent={staffCommissionPercent}
          rawRef={rawRef}
        />
      </Suspense>
    );
  }

  // ─── Minimal theme: render completely separate layout ───
  if (activeTheme === "minimal") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <MinimalStorefront
          referralStaff={referralStaff}
          referralInvalid={referralInvalid}
          isStaffLocked={isStaffLocked}
          showContactOnly={showContactOnly}
          staffCommissionPercent={staffCommissionPercent}
          rawRef={rawRef}
        />
      </Suspense>
    );
  }

  // ─── Classic theme (original layout, untouched) ───
  return <ClassicStorefront referralStaff={referralStaff} referralInvalid={referralInvalid} isStaffLocked={isStaffLocked} showContactOnly={showContactOnly} staffCommissionPercent={staffCommissionPercent} rawRef={rawRef} />;
};
export default Index;
